import React, { useEffect, useState } from "react";
import { Container } from "reactstrap";

//Import Components
// Redux
import { Box, Grid } from "@mui/material";
import { connect, useDispatch } from 'react-redux';
import { useNavigate } from "react-router-dom";
import Notification from "../../components/Common/Notification";
import Table from "../../components/Common/Table";
import CustomButton from "../../components/Form/CustomButton";
import CustomSelect from "../../components/Form/CustomSelect";
import CustomTextField from "../../components/Form/CustomTextField";
import { calculateLoan, resetLoanCalculateState } from "../../store/actions";
import LoadingDialog from "../../components/Common/LoadingDialog";
import Helpers from "../../helpers/helpers";

const LoanCalculator = ({ loanListing, leftSideBarType, calculateResult, loanInterestRatesList }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [loading, setIsLoading] = useState(false);
    const [width, setWidth] = useState(window.innerWidth);
    const [values, setFormValues] = useState({});
    const [notification, setNotification] = useState({
        open: false,
        msg: "",
        type: "success",
    });

    useEffect(() => {
        const handleResize = () => {
            setWidth(window.innerWidth); // Update state when window resizes
        };
        window.addEventListener("resize", handleResize);
        return (() => {
            window.removeEventListener("resize", handleResize); // Cleanup on unmount
        })
    }, []);

    useEffect(() => {
        dispatch(resetLoanCalculateState());
    }, [])

    useEffect(() => {
        if (calculateResult?.code === 'error') {
            setIsLoading(false);
            setNotification({
                open: true,
                msg: calculateResult?.message || "Failed to calculate loan. Please try again.",
                type: "error",
            });
        } else if (calculateResult?.code === 'success') {
            setIsLoading(false);
        }
    }, [calculateResult])

    const header = [
        {
            Header: "Year",
            accessor: (cellProps) => {
                return (cellProps?.year || "-")
            },
        },
        {
            Header: "Interest ($)",
            accessor: (cellProps) => {
                return (cellProps?.interest || "-")
            },
        },
        {
            Header: "Monthly Instalment ($)",
            accessor: (cellProps) => {
                return (<div style={{ display: "flex", justifyContent: "flex-end" }}>{cellProps?.monthly || "-"}</div>)
            },
        },
        {
            Header: "Last Instalment ($)",
            accessor: (cellProps) => {
                return (<div style={{ display: "flex", justifyContent: "flex-end" }}>{cellProps?.last || "-"}</div>)
            },
        },
    ]

    const handleCalculate = () => {
        setIsLoading(true);
        let payload = {
            price: Number(values?.price || 0),
            overtrade: Number(values?.overtrade || 0),
            coe: Number(values?.coe || 0),
            insurance: Number(values?.insurance || 0),
            fee: Number(values?.fee || 0),
            other: Number(values?.other || 0),
            downpayment: Number(values?.downpayment || 0),
            term: Number(values?.term || 0),
            rate: Number(values?.rate || 0),
        }
        dispatch(calculateLoan(payload));
    }

    const calculateTotalPrice = () => {
        let total = 0;
        total += parseFloat(values?.price || 0);
        total += parseFloat(values?.overtrade || 0);
        total += parseFloat(values?.coe || 0);
        total += parseFloat(values?.insurance || 0);
        total += parseFloat(values?.fee || 0);
        total += parseFloat(values?.other || 0);
        return total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }

    const handleFormChange = (key, value) => {
        setFormValues((prev) => ({
            ...prev,
            [key]: value,
        }));
    }

    return (
        <React.Fragment>
            <div className="page-content" style={{ marginTop: width <= 768 && leftSideBarType === 'condensed' ? 72 : 0 }}>
                <LoadingDialog open={loading} />
                <Notification notification={notification} setNotification={setNotification} />
                <Container fluid style={{}}>
                    <div style={{ marginBottom: "10px" }} className="header-2 d-flex align-items-center">Loan Calculator</div>
                    <span className="text-muted pb-3">Use this calculator to estimate monthly instalments and loan details for your customers.</span>
                    <Grid container spacing={2} mt={2}>
                        <Grid item lg={6} md={6} sm={12} xs={12} pr={2}>
                            <Grid container spacing={1}>
                                <Grid item xs={12} style={{ fontWeight: 600, }}>
                                    Vehicle Pricing
                                </Grid>

                                <Grid item sm={6} xs={12}>
                                    <CustomTextField
                                        placeholder={"Machine Price"}
                                        title={"Machine Price"}
                                        type={"number"}
                                        is2dp={true}
                                        values={values?.price || ""}
                                        onChange={(e) => handleFormChange('price', e)}
                                        InputProps={{
                                            startAdornment: <div style={{ paddingLeft: 6, paddingRight: 10 }}>$ </div>,
                                        }}
                                    />
                                </Grid>
                                <Grid item sm={6} xs={12}>
                                    <CustomTextField
                                        placeholder={"Overtrade"}
                                        title={"Overtrade"}
                                        type={"number"}
                                        is2dp={true}
                                        InputProps={{
                                            startAdornment: <div style={{ paddingLeft: 6, paddingRight: 10 }}>$ </div>,
                                        }}
                                        values={values?.overtrade || ""}
                                        onChange={(e) => handleFormChange('overtrade', e)}
                                    />
                                </Grid>
                                <Grid item sm={6} xs={12}>
                                    <CustomTextField
                                        placeholder={"COE"}
                                        title={"COE"}
                                        type={"number"}
                                        is2dp={true}
                                        InputProps={{
                                            startAdornment: <div style={{ paddingLeft: 6, paddingRight: 10 }}>$ </div>,
                                        }}
                                        values={values?.coe || ""}
                                        onChange={(e) => handleFormChange('coe', e)}
                                    />
                                </Grid>
                                <Grid item sm={6} xs={12}>
                                    <CustomTextField
                                        placeholder={"Insurance"}
                                        title={"Insurance"}
                                        type={"number"}
                                        is2dp={true}
                                        InputProps={{
                                            startAdornment: <div style={{ paddingLeft: 6, paddingRight: 10 }}>$ </div>,
                                        }}
                                        values={values?.insurance || ""}
                                        onChange={(e) => handleFormChange('insurance', e)}
                                    />
                                </Grid>
                                <Grid item sm={6} xs={12}>
                                    <CustomTextField
                                        placeholder={"Admin Fee"}
                                        title={"Admin Fee"}
                                        type={"number"}
                                        is2dp={true}
                                        InputProps={{
                                            startAdornment: <div style={{ paddingLeft: 6, paddingRight: 10 }}>$ </div>,
                                        }}
                                        values={values?.fee || ""}
                                        onChange={(e) => handleFormChange('fee', e)}
                                    />
                                </Grid>
                                <Grid item sm={6} xs={12}>
                                    <CustomTextField
                                        placeholder={"Other Charges"}
                                        title={"Other Charges"}
                                        type={"number"}
                                        is2dp={true}
                                        InputProps={{
                                            startAdornment: <div style={{ paddingLeft: 6, paddingRight: 10 }}>$ </div>,
                                        }}
                                        values={values?.other || ""}
                                        onChange={(e) => handleFormChange('other', e)}
                                    />
                                </Grid>
                                <Grid item xs={12} display={"flex"} justifyContent={"flex-end"} pt={5}>
                                    <span className="text-muted">Total price: </span> <span className="text-bold mx-1">  ${calculateTotalPrice()}</span>
                                </Grid>

                                <Grid item xs={12} style={{ fontWeight: 600, paddingTop: 20 }}>
                                    Loan Details
                                </Grid>

                                <Grid item xs={12}>
                                    <CustomTextField
                                        placeholder={"Downpayment"}
                                        title={"Downpayment"}
                                        type={"number"}
                                        is2dp={true}
                                        values={values?.downpayment || ""}
                                        onChange={(e) => handleFormChange('downpayment', e)}
                                        InputProps={{
                                            startAdornment: <div style={{ paddingLeft: 6, paddingRight: 10 }}>$ </div>,
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <CustomSelect
                                        placeholder={"Loan Terms (mth)"}
                                        title={"Loan Terms (mth)"}
                                        values={values?.term || ""}
                                        data={Helpers.loanTermOptions}
                                        onChange={(e) => handleFormChange('term', e)}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <CustomSelect
                                        placeholder={"Interest Rate p.a."}
                                        title={"Interest Rate p.a."}
                                        data={loanInterestRatesList || []}
                                        labelName={'interest_rate'}
                                        valueName={'interest_rate'}
                                        values={values?.rate || ""}
                                        onChange={(e) => handleFormChange('rate', e)}
                                    />
                                </Grid>
                                <Grid item xs={12} mt={2}>
                                    <CustomButton
                                        title={"Calculate"}
                                        press={(e) => handleCalculate()}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid item lg={6} md={6} sm={12} xs={12}>
                            <span style={{ fontWeight: 600, paddingTop: 20 }}>
                                Instalment Reference
                            </span>
                            <Box style={{ display: "flex", flexDirection: "column", paddingTop: "30px", padding: "10px", paddingBottom: "15px" }} >
                                <span style={{ fontWeight: 600, color: "#D13536", fontSize: 32 }}>{calculateResult?.data?.monthly_instal || "-"}</span>
                                <span className="text-muted">Est. Monthly Instalment</span>
                                <Grid container spacing={2} py={2}>
                                    <Grid item lg={4} md={4} sm={4} xs={4}>
                                        <div style={{ fontWeight: 600, fontSize: 24 }}>{calculateResult?.data?.loan_amount || "-"}</div>
                                        <div className="text-muted">Loan Amount</div>
                                    </Grid>
                                    <Grid item lg={4} md={4} sm={4} xs={4}>
                                        <div style={{ fontWeight: 600, fontSize: 24 }}>{calculateResult?.data?.interest || "-"}</div>
                                        <div className="text-muted">Interest</div>
                                    </Grid>
                                    <Grid item lg={4} md={4} sm={4} xs={4}>
                                        <div style={{ fontWeight: 600, fontSize: 24 }}>{calculateResult?.data?.grand_total || "-"}</div>
                                        <div className="text-muted">Grand Total</div>
                                    </Grid>
                                </Grid>
                            </Box>
                            <Table
                                header={header}
                                data={calculateResult?.data?.instal_ref || []}
                                pageSize={calculateResult?.data?.instal_ref?.length || 10}
                                totalPage={calculateResult?.data?.last_page}
                                headerBackground={"#F5F5F5"}
                                headerTitleColor={"#808588"}
                            />
                            <span className="text-muted pt-5">Note: The results are indicative and may vary based on bank approval.</span>
                        </Grid>

                    </Grid>
                </Container>
            </div >
            {/* <Footer></Footer> */}
        </React.Fragment >
    );

}

function mapStateToProps({ Login, LoanCalculate, Layout, General }) {
    return {
        userPermission: Login.permissionArry,
        leftSideBarType: Layout.leftSideBarType,
        calculateResult: LoanCalculate.calculateResult,
        loanInterestRatesList: General.loan_interest_rates,
    };
}

export default connect(mapStateToProps)(LoanCalculator);
